<!--
  AscendQE: hiring pipeline (Kanban) for Recruitment > Candidates.

  Moving a card runs the real recruitment workflow:
    - simple transitions (shortlist / offer / hire / decline / reject) ask for an
      optional note in a dialog, then PUT the action endpoint;
    - transitions that need more detail (schedule interview, pass / fail) open
      the stock action page;
    - transitions the server does not allow (GET .../actions/allowed) snap back.
  Mouse: HTML5 drag & drop. Keyboard: Space/Enter lifts a card, ←/→ choose a
  column, Space/Enter drops, Esc cancels (announced via aria-live).
-->
<template>
  <div class="aqe-board" :aria-busy="String(loading)">
    <p class="aqe-sr" aria-live="assertive">{{ announce }}</p>
    <div class="aqe-board__cols">
      <section
        v-for="col in columns"
        :key="col.key"
        class="aqe-col"
        :class="{
          'is-target': dropKey === col.key,
          'is-lift-target': lifted && liftCol === col.key,
        }"
        :aria-label="`${col.title}, ${grouped[col.key].length} candidates`"
        @dragover.prevent="onDragOver(col)"
        @dragleave="onDragLeave(col, $event)"
        @drop.prevent="onDrop(col)"
      >
        <header class="aqe-col__head">
          <span class="aqe-col__dot" :style="{background: col.color}"></span>
          <span class="aqe-col__title">{{ col.title }}</span>
          <span class="aqe-col__count">{{ grouped[col.key].length }}</span>
        </header>
        <div class="aqe-col__body">
          <template v-if="loading && !candidates.length">
            <div
              v-for="n in 3"
              :key="n"
              class="aqe-card aqe-card--skel"
              aria-hidden="true"
            >
              <span></span><span></span>
            </div>
          </template>
          <article
            v-for="c in grouped[col.key]"
            :key="c.id"
            class="aqe-card"
            :class="{
              'is-dragging': dragId === c.id,
              'is-lifted': lifted && lifted.id === c.id,
              'is-busy': busyId === c.id,
            }"
            draggable="true"
            tabindex="0"
            role="button"
            :aria-roledescription="'Draggable candidate card'"
            :aria-label="`${c.name}, ${
              c.vacancy || 'no vacancy'
            }, ${statusLabel(c.status)}`"
            :aria-grabbed="String(!!(lifted && lifted.id === c.id))"
            @dragstart="onDragStart($event, c)"
            @dragend="onDragEnd"
            @click="open(c)"
            @keydown="onCardKey($event, c, col)"
          >
            <div class="aqe-card__top">
              <span class="aqe-card__avatar" :data-hue="c.id % 6">{{
                initials(c.name)
              }}</span>
              <div class="aqe-card__who">
                <span class="aqe-card__name">{{ c.name }}</span>
                <span class="aqe-card__vac">{{ c.vacancy || '—' }}</span>
              </div>
              <i
                v-if="c.resume"
                class="oxd-icon bi-paperclip aqe-card__clip"
                title="Resume attached"
              ></i>
            </div>
            <div class="aqe-card__meta">
              <span
                v-if="col.key === 'interview' || col.key === 'closed'"
                class="aqe-card__tag"
                :data-s="c.status"
              >
                {{ statusLabel(c.status) }}
              </span>
              <span class="aqe-card__date"
                ><i class="oxd-icon bi-calendar3"></i>{{ c.applied }}</span
              >
            </div>
            <span
              v-if="busyId === c.id"
              class="aqe-card__spin"
              aria-hidden="true"
            ></span>
          </article>
          <p v-if="!loading && !grouped[col.key].length" class="aqe-col__empty">
            {{ dragId ? 'Drop here' : 'No candidates' }}
          </p>
        </div>
      </section>
    </div>

    <!-- note dialog -->
    <teleport to="#app">
      <oxd-dialog
        v-if="pending"
        class="aqe-note-dialog"
        @update:show="cancelPending"
      >
        <div class="aqe-note">
          <h3>{{ pending.title }}</h3>
          <p>
            Move <strong>{{ pending.candidate.name }}</strong> from
            {{ statusLabel(pending.candidate.status) }} to
            <strong>{{ pending.to }}</strong
            >.
          </p>
          <label class="oxd-label" for="aqe-note-text">Note (optional)</label>
          <textarea
            id="aqe-note-text"
            ref="noteText"
            v-model="note"
            class="oxd-textarea oxd-textarea--active"
            rows="3"
            maxlength="2000"
            placeholder="Add context for the hiring team…"
          ></textarea>
          <div class="aqe-note__actions">
            <oxd-button
              display-type="ghost"
              :label="$t('general.cancel')"
              @click="cancelPending"
            />
            <oxd-button
              :display-type="pending.danger ? 'label-danger' : 'secondary'"
              :label="pending.confirm"
              @click="confirmPending"
            />
          </div>
        </div>
      </oxd-dialog>
    </teleport>
  </div>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';

const ACTION = {
  SHORTLIST: 2,
  REJECT: 3,
  SCHEDULE: 4,
  PASS: 5,
  FAIL: 6,
  OFFER: 7,
  DECLINE: 8,
  HIRE: 9,
};
const ENDPOINT = {
  2: 'shortlist',
  3: 'reject',
  7: 'job/offer',
  8: 'job/decline',
  9: 'hire',
};

export default {
  name: 'PipelineBoard',
  components: {'oxd-dialog': OxdDialog},
  props: {
    candidates: {type: Array, default: () => []},
    loading: {type: Boolean, default: false},
    statuses: {type: Array, required: true},
  },
  emits: ['changed'],

  setup() {
    return {http: new APIService(window.appGlobal.baseUrl, '')};
  },

  data() {
    return {
      dragId: null,
      dropKey: null,
      busyId: null,
      lifted: null,
      liftCol: null,
      pending: null,
      note: '',
      announce: '',
      columns: [
        {key: 'applied', title: 'Applied', statuses: [1], color: '#94a3b8'},
        {
          key: 'shortlisted',
          title: 'Shortlisted',
          statuses: [2],
          color: '#6366f1',
        },
        {
          key: 'interview',
          title: 'Interview',
          statuses: [4, 5, 6],
          color: '#0ea5e9',
        },
        {key: 'offered', title: 'Offered', statuses: [7], color: '#f59e0b'},
        {key: 'hired', title: 'Hired', statuses: [9], color: '#10b981'},
        {key: 'closed', title: 'Closed', statuses: [3, 8], color: '#ef4444'},
      ],
    };
  },

  computed: {
    grouped() {
      const g = Object.fromEntries(this.columns.map((c) => [c.key, []]));
      this.candidates.forEach((c) => {
        const col = this.columns.find((x) => x.statuses.includes(c.status));
        if (col) g[col.key].push(c);
      });
      return g;
    },
  },

  methods: {
    statusLabel(id) {
      return this.statuses.find((s) => s.id === id)?.label || '';
    },
    initials(name) {
      return name
        .split(/\s+/)
        .filter(Boolean)
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    colOf(c) {
      return this.columns.find((x) => x.statuses.includes(c.status));
    },
    open(c) {
      if (this.dragId || this.lifted) return;
      navigate('/recruitment/addCandidate/{id}', {id: c.id});
    },

    // ── mouse drag & drop ──
    onDragStart(e, c) {
      this.dragId = c.id;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(c.id));
    },
    onDragEnd() {
      this.dragId = null;
      this.dropKey = null;
    },
    onDragOver(col) {
      if (this.dragId) this.dropKey = col.key;
    },
    onDragLeave(col, e) {
      if (
        !e.currentTarget.contains(e.relatedTarget) &&
        this.dropKey === col.key
      )
        this.dropKey = null;
    },
    onDrop(col) {
      const c = this.candidates.find((x) => x.id === this.dragId);
      this.onDragEnd();
      if (c) this.move(c, col);
    },

    // ── keyboard drag & drop ──
    onCardKey(e, c, col) {
      const idx = this.columns.findIndex(
        (x) => x.key === (this.liftCol || col.key),
      );
      if (!this.lifted) {
        if (e.key === ' ' || e.key === 'Enter') {
          if (e.key === 'Enter' && !e.shiftKey) return this.open(c);
          e.preventDefault();
          this.lifted = c;
          this.liftCol = col.key;
          this.announce = `Picked up ${c.name}. Use left and right arrows to choose a column, space to drop, escape to cancel.`;
        }
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const next =
          this.columns[
            Math.max(
              0,
              Math.min(
                this.columns.length - 1,
                idx + (e.key === 'ArrowRight' ? 1 : -1),
              ),
            )
          ];
        this.liftCol = next.key;
        this.announce = `Over ${next.title}`;
      } else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        const target = this.columns.find((x) => x.key === this.liftCol);
        const card = this.lifted;
        this.lifted = null;
        this.liftCol = null;
        this.move(card, target);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.announce = `Cancelled. ${this.lifted.name} stays in ${
          this.colOf(this.lifted).title
        }.`;
        this.lifted = null;
        this.liftCol = null;
      }
    },

    // ── workflow ──
    actionFor(c, col) {
      switch (col.key) {
        case 'shortlisted':
          return ACTION.SHORTLIST;
        case 'interview':
          return c.status === 4 ? null : ACTION.SCHEDULE;
        case 'offered':
          return ACTION.OFFER;
        case 'hired':
          return ACTION.HIRE;
        case 'closed':
          return c.status === 7 ? ACTION.DECLINE : ACTION.REJECT;
        default:
          return null;
      }
    },
    async move(c, col) {
      const from = this.colOf(c);
      if (!col || col.key === from.key) return;
      const action = this.actionFor(c, col);
      const reject = (msg) => {
        this.announce = msg;
        this.$toast.warn({title: this.$t('general.warning'), message: msg});
      };
      if (!action)
        return reject(
          `${c.name} can't move from ${from.title} to ${col.title}`,
        );

      this.busyId = c.id;
      let allowed = [];
      try {
        const {data} = await this.http.request({
          method: 'GET',
          url: `/api/v2/recruitment/candidates/${c.id}/actions/allowed`,
        });
        allowed = (data.data || []).map((a) => Number(a.id));
      } catch (e) {
        allowed = [];
      } finally {
        this.busyId = null;
      }
      if (!allowed.includes(action)) {
        return reject(
          `${c.name} can't move from ${this.statusLabel(c.status)} to ${
            col.title
          }`,
        );
      }
      if (!ENDPOINT[action]) {
        // needs interview details: hand over to the stock action page
        return navigate(
          '/recruitment/changeCandidateVacancyStatus',
          {},
          {candidateId: c.id, selectedAction: action},
        );
      }
      const danger = action === ACTION.REJECT || action === ACTION.DECLINE;
      this.note = '';
      this.pending = {
        candidate: c,
        action,
        to: col.title,
        danger,
        title: {
          2: 'Shortlist candidate',
          3: 'Reject candidate',
          7: 'Offer job',
          8: 'Mark offer declined',
          9: 'Hire candidate',
        }[action],
        confirm: {
          2: 'Shortlist',
          3: 'Reject',
          7: 'Offer job',
          8: 'Mark declined',
          9: 'Hire',
        }[action],
      };
      this.$nextTick(() => this.$refs.noteText?.focus());
    },
    cancelPending() {
      if (this.pending)
        this.announce = `${this.pending.candidate.name} was not moved.`;
      this.pending = null;
    },
    async confirmPending() {
      const {candidate, action, to} = this.pending;
      this.pending = null;
      this.busyId = candidate.id;
      try {
        await this.http.request({
          method: 'PUT',
          url: `/api/v2/recruitment/candidates/${candidate.id}/${ENDPOINT[action]}`,
          data: {note: this.note || null},
        });
        this.announce = `${candidate.name} moved to ${to}`;
        this.$toast.updateSuccess();
        this.$emit('changed');
      } finally {
        this.busyId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$ink: #0f172a;
$ink3: #64748b;
$ink4: #94a3b8;

.aqe-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}
.aqe-board {
  padding: 0 24px 24px;
  font-family: Inter, 'Nunito Sans', sans-serif;
}
.aqe-board__cols {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(250px, 1fr);
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 6px;
}
.aqe-col {
  display: flex;
  flex-direction: column;
  min-height: 360px;
  max-height: 68vh;
  border-radius: 16px;
  background: #f1f5f9;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.04);
  transition: background-color 0.15s, box-shadow 0.15s;
  &.is-target,
  &.is-lift-target {
    background: rgba(99, 102, 241, 0.08);
    box-shadow: inset 0 0 0 2px rgba(99, 102, 241, 0.5);
  }
}
.aqe-col__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 14px 10px;
}
.aqe-col__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.aqe-col__title {
  flex: 1;
  color: $ink;
  font: 700 13.5px Inter, sans-serif;
}
.aqe-col__count {
  min-width: 24px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff;
  color: $ink3;
  font: 600 12px Inter, sans-serif;
  text-align: center;
}
.aqe-col__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 10px 12px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.aqe-col__empty {
  margin: 20px 0;
  color: $ink4;
  font-size: 13px;
  text-align: center;
}
.aqe-card {
  position: relative;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.05);
  cursor: grab;
  transition: box-shadow 0.15s, transform 0.15s, opacity 0.15s;
  &:hover {
    box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.3),
      0 8px 18px -10px rgba(15, 23, 42, 0.35);
    transform: translateY(-1px);
  }
  &:focus-visible {
    outline: 2px solid #6366f1;
    outline-offset: 2px;
  }
  &.is-dragging {
    opacity: 0.4;
  }
  &.is-lifted {
    transform: rotate(-1.5deg) scale(1.02);
    box-shadow: 0 0 0 2px #6366f1, 0 18px 30px -12px rgba(15, 23, 42, 0.4);
  }
  &.is-busy {
    opacity: 0.6;
    pointer-events: none;
  }
  &--skel span {
    display: block;
    height: 10px;
    margin: 6px 0;
    border-radius: 5px;
    background: #eef1f5;
    &:first-child {
      width: 60%;
    }
  }
}
.aqe-card__top {
  display: flex;
  gap: 10px;
  align-items: center;
}
.aqe-card__avatar {
  flex: 0 0 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #e0e7ff;
  color: #3730a3;
  font: 700 11.5px Inter, sans-serif;
  &[data-hue='1'] {
    background: #dcfce7;
    color: #166534;
  }
  &[data-hue='2'] {
    background: #fef3c7;
    color: #92400e;
  }
  &[data-hue='3'] {
    background: #fce7f3;
    color: #9d174d;
  }
  &[data-hue='4'] {
    background: #e0f2fe;
    color: #075985;
  }
  &[data-hue='5'] {
    background: #ede9fe;
    color: #5b21b6;
  }
}
.aqe-card__who {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.aqe-card__name {
  color: $ink;
  font: 600 13.5px Inter, sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aqe-card__vac {
  color: $ink3;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aqe-card__clip {
  color: $ink4;
}
.aqe-card__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-top: 10px;
}
.aqe-card__tag {
  white-space: nowrap;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #075985;
  font-size: 11px;
  font-weight: 600;
  &[data-s='5'] {
    background: #dcfce7;
    color: #166534;
  }
  &[data-s='6'],
  &[data-s='3'],
  &[data-s='8'] {
    background: #fee2e2;
    color: #991b1b;
  }
}
.aqe-card__date {
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  color: $ink4;
  font-size: 11.5px;
}
.aqe-card__spin {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(15, 23, 42, 0.12);
  border-top-color: #6366f1;
  animation: aqe-spin 0.8s linear infinite;
}
@keyframes aqe-spin {
  to {
    transform: rotate(360deg);
  }
}
.aqe-note {
  min-width: min(440px, 80vw);
  font-family: Inter, 'Nunito Sans', sans-serif;
  h3 {
    margin: 0 0 6px;
    color: $ink;
    font-size: 18px;
  }
  p {
    margin: 0 0 14px;
    color: $ink3;
    font-size: 14px;
  }
  textarea {
    width: 100%;
    margin-top: 6px;
    padding: 10px 12px;
    box-sizing: border-box;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 12px;
    font: 500 14px Inter, 'Nunito Sans', sans-serif;
    color: #0f172a;
    resize: vertical;
    outline: none;
    &:focus {
      border-color: #6366f1;
      box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.14);
    }
  }
}
.aqe-note__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
</style>
